import { stringify } from "qs";
import { getHeaders, makeRequest, fetch } from "$utils/utils";
import config from "./config";
import { Fiability } from "$types/Fiability";
import { DockyardStatus } from "$types/DockyardStatus";
import { GetAllResponse } from "$types/Request";
import { Dockyard } from "$types";
import { Services, SERVICES_URL } from "$utils/typeConstants";

const url = config.api_url;

export enum ERRORS {
  MISSING_FIELD = "Donnée manquante !",
  ADD_ERROR = "Une erreur s'est produite lors de l'ajout du chantier",
  UPLOAD_MISSING_FIELD = "Le fichier que vous essayez d’importer contient un ou plusieurs champs obligatoires vides (indiqués par un astérisque *). Veuillez apporter les modifications requises et importer de nouveau le fichier.",
  DOCK_EXISTS = "Cette entreprise a déjà un chantier existant avec ce code chantier, veuillez modifier les informations ou annuler la création",
  UPDATE_ERROR = "Une erreur s'est produite lors de l'édition du chantier",
  UPLOAD_EXIST = "Le fichier que vous essayez d'importer contient un ou plusieurs chantiers avec le même code chantier que l'un des chantiers existants en base pour cette entreprise. Veuillez svp les modifier",
  IMPORT_DUPLICATE = "Le fichier que vous essayez d’uploader contient des doublons. Veuillez le modifier afin d’importer les chantiers",
}

export interface IDockyard {
  _id?: string;
  project?: string;
  code: string;
  codeAnalytics?: string;
  name: string;
  address: string;
  address2?: string;
  postalCode: string;
  city: string;
  country: string;
  company: any;
  contact: string;
  fiability: Fiability;
  status: DockyardStatus;
  date: string;
  lastnameContact?: string;
  firstnameContact?: string;
  emailContact?: string;
  phoneContact?: string;
  isDisable?: boolean;
  updateAt: string;
  projectDisable?: boolean;
  companyDisable?: boolean;
  notes?: string;
  interveningCompany?: any[];
  medias?: string[];
}

export const AddDockyard = async (token: string, data: any) => {
  try {
    const res = await makeRequest(
      `${url}/api/dockyards`,
      "POST",
      data,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error(ERRORS.ADD_ERROR);
    }
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data?.message;
    if (error && error.includes("already exists")) {
      error = ERRORS.DOCK_EXISTS;
    } else {
      error = ERRORS.ADD_ERROR;
    }
    return { error };
  }
};

export const UpdateDockyard = async (token: string, data: any, id?: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/dockyards/${id}`,
      "PUT",
      data,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error(ERRORS.ADD_ERROR);
    }
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data?.message;
    if (error && error.includes("already exists")) {
      error = ERRORS.DOCK_EXISTS;
    } else {
      error = ERRORS.ADD_ERROR;
    }
    return { error };
  }
};

export const UpdateDockyardStatus = async (
  token: string,
  data: any,
  id?: string
) => {
  try {
    const res = await makeRequest(
      `${url}/api/dockyards/status/${id}`,
      "POST",
      data,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error(ERRORS.ADD_ERROR);
    }
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data?.message;
    if (error && error.includes("already exists")) {
      error = ERRORS.DOCK_EXISTS;
    } else {
      error = ERRORS.ADD_ERROR;
    }
    return { error };
  }
};

export const DisabledDockyard = async (token: string, id?: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/dockyards/disable/${id}`,
      "POST",
      {},
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error(ERRORS.ADD_ERROR);
    }
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data?.message;
    if (error && error.includes("already exists")) {
      error = ERRORS.DOCK_EXISTS;
    } else {
      error = ERRORS.ADD_ERROR;
    }
    return { error };
  }
};

export const EnableDockyard = async (token: string, id?: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/dockyards/enable/${id}`,
      "POST",
      {},
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error(ERRORS.ADD_ERROR);
    }
    return res.data.data;
  } catch (err) {
    let error = (err as any)?.response?.data?.message;
    if (error && error.includes("already exists")) {
      error = ERRORS.DOCK_EXISTS;
    } else {
      error = ERRORS.ADD_ERROR;
    }
    return { error };
  }
};

// pagination
export const getDockyards = async (params) => {
  try {
    const { data } = await fetch(
      `/api/dockyards?${stringify(params, { arrayFormat: "brackets" })}`
    );
    return data;
  } catch (err) {
    throw new Error(err?.message);
  }
};

// TODO: replace GetDockyard with getProjectById
export const getDockyardById = async (id: string) => {
  try {
    const { data } = await fetch(`/api/dockyards/${id}`);
    return data.data;
  } catch (err) {
    throw new Error(err?.message);
  }
};

export const getDockyardsWithWarehouse = async (params: {
  companyId?: string;
  warehouseId?: string;
  nameCode?: string;
  isActive?: boolean;
  companyDisable?: boolean;
  projectDisable?: boolean;
  projectId?: string;
  rowsPerPage?: number;
  page?: number;
}): Promise<GetAllResponse<Dockyard[]>> => {
  try {
    const response = await fetch(
      `/api/dockyard-list/warehouse?${stringify(params)}`,
      {},
      SERVICES_URL[Services.GATEWAY_API]
    );
    return response.data;
  } catch (err) {
    throw new Error(err?.message);
  }
};

export const GetDockyard = async (token: string, id: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/dockyards/${id}`,
      "GET",
      undefined,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error(ERRORS.ADD_ERROR);
    }
    return res.data.data;
  } catch (err) {
    return [];
  }
};

// export const GetDockyard = async (token: string, id: string) => {
//   try {
//     const res = await makeRequest(
//       `${url}/api/dockyards/${id}`,
//       "GET",
//       undefined,
//       getHeaders(token)
//     );
//     if (!res || !res.data || !res.data.status) {
//       throw new Error(ERRORS.ADD_ERROR);
//     }
//     return res.data.data;
//   } catch (err) {
//     return [];
//   }
// };

export const ImportDockyard = async (token: string, data: any, id: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/dockyards/import/${id}`,
      "POST",
      data,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error(ERRORS.ADD_ERROR);
    }
    return res.data.data;
  } catch (err: any) {
    const error = err?.response?.data?.message;
    if (error && error.includes("already exists")) {
      return { error: ERRORS.UPLOAD_EXIST };
    }
    if (error && error.includes("missing")) {
      return { error: ERRORS.UPLOAD_MISSING_FIELD };
    }
    if (error && error.includes("duplicate code")) {
      return { error };
    }
    return { error: ERRORS.ADD_ERROR };
  }
};

export const deleteDockyard = async (token: string, id: string) => {
  try {
    const res = await makeRequest(
      `${url}/api/dockyards/${id}`,
      "DELETE",
      undefined,
      getHeaders(token)
    );
    if (!res || !res.data || !res.data.status) {
      throw new Error("Error when deleting dockyard");
    }
    return res.data.data;
  } catch (err) {
    throw new Error(ERRORS.UPDATE_ERROR);
  }
};
